// Реальное время: WebSocket с автоматическим переподключением. Экран подписывается на типы событий
// и перечитывает данные — без кнопки «Обновить».

import { useEffect, useRef, useState } from "react";
import { Api } from "./api";

export type LiveEvent = { type: string; id?: string; scope?: string[] | null; at?: string };
type Handler = (e: LiveEvent) => void;

export class Live {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>();
  private statusListeners = new Set<(online: boolean) => void>();
  private retry = 0;
  private stopped = false;
  online = false;

  constructor(private api: Api) {}

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.ws?.close();
  }

  private setOnline(v: boolean): void {
    this.online = v;
    this.statusListeners.forEach((l) => l(v));
  }

  private connect(): void {
    const token = this.api.token;
    if (!token || this.stopped) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}${this.api.base}/ws?token=${encodeURIComponent(token)}`);
    this.ws = ws;
    let ping: number | undefined;
    ws.onopen = () => {
      this.retry = 0;
      this.setOnline(true);
      ping = window.setInterval(() => ws.readyState === 1 && ws.send("ping"), 25_000);
    };
    ws.onmessage = (m) => {
      try {
        const e = JSON.parse(m.data) as LiveEvent;
        if (e.type !== "hello" && e.type !== "pong") this.handlers.forEach((h) => h(e));
      } catch {
        /* не JSON — пропускаем */
      }
    };
    ws.onclose = () => {
      window.clearInterval(ping);
      this.setOnline(false);
      if (this.stopped) return;
      const delay = Math.min(15_000, 1000 * 2 ** this.retry++);
      setTimeout(() => this.connect(), delay);
    };
  }

  on(h: Handler): () => void {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  onStatus(l: (online: boolean) => void): () => void {
    this.statusListeners.add(l);
    l(this.online);
    return () => this.statusListeners.delete(l);
  }
}

/** Перечитать данные, когда пришли события указанных типов (префиксы: "order.", "trip."). */
export function useLive(live: Live, prefixes: string[], onChange: (e: LiveEvent) => void, debounceMs = 300): void {
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    let t: number | undefined;
    let last: LiveEvent | null = null;
    return live.on((e) => {
      if (!prefixes.some((p) => e.type.startsWith(p))) return;
      last = e;
      window.clearTimeout(t);
      t = window.setTimeout(() => last && cb.current(last), debounceMs);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, prefixes.join("|")]);
}

export function useOnline(live: Live): boolean {
  const [online, setOnline] = useState(live.online);
  useEffect(() => live.onStatus(setOnline), [live]);
  return online;
}
